import React, { useEffect } from 'react';
import {
  Paper, Box, Typography, Grid, Chip, Button, Divider
} from '@mui/material';
import CreditCardIcon from '@mui/icons-material/CreditCard';
import SwapHorizIcon from '@mui/icons-material/SwapHoriz';
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet';
import CardGiftcardIcon from '@mui/icons-material/CardGiftcard';
import AddCircleIcon from '@mui/icons-material/AddCircle';

interface WalletBalance {
  walletId: number;
  walletName: string;
  currency: string;
  balance: number;
}

interface ActiveSession {
  id: number;
  status: string;
  accountName: string;
  /** Sahiplik türüne göre çözülmüş ad: cari adı ya da personel adı. */
  ownerName?: string;
  ownerType?: 'ACCOUNT' | 'PERSONNEL';
  walletBalances: WalletBalance[];
  totalBalance: number;
}

interface CardData {
  id: number;
  cardNumber: string;
  cardUID?: string;
  cardTypeName?: string;
  cardTypeColor?: string;
  accountName: string;
  ownerName?: string;
  ownerType?: 'ACCOUNT' | 'PERSONNEL';
  activeSession?: ActiveSession;
  wallets: WalletBalance[];
}

interface EntitlementItem {
  id: number;
  productId: string;
  productName: string;
  quantity: number;
  consumedQuantity: number;
  remaining: number;
  unitPricePaid: number;
  status: string;
}

interface Props {
  card: CardData | null;
  selectedWalletId: number | null;
  onWalletSelect: (walletId: number) => void;
  onChangeCard: () => void;
  cartTotal?: number;
  /** Karta tanımlı ön ödemeli ürün hakları (girişte ödenmiş). */
  entitlements?: EntitlementItem[];
  /** Tanımlı ürüne tıklanınca sepete 0 TL ekler. */
  onAddEntitlement?: (ent: EntitlementItem) => void;
}

const formatCurrency = (amount: number) => {
  return new Intl.NumberFormat('tr-TR', { style: 'currency', currency: 'TRY' }).format(amount);
};

const PrepaidCardPanel: React.FC<Props> = ({ card, selectedWalletId, onWalletSelect, onChangeCard, cartTotal = 0, entitlements = [], onAddEntitlement }) => {
  const wallets = card?.wallets || card?.activeSession?.walletBalances || [];

  useEffect(() => {
    if (card && wallets.length === 1 && !selectedWalletId) {
      onWalletSelect(wallets[0].walletId);
    }
  }, [card, wallets, selectedWalletId, onWalletSelect]);

  if (!card) {
    return (
      <Paper elevation={1} sx={{ p: 1.5, mb: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
          <Typography variant="subtitle2" sx={{ display: 'flex', alignItems: 'center' }}>
            <CreditCardIcon sx={{ fontSize: '1rem', mr: 0.5 }} />
            Ön Ödemeli Kart
          </Typography>
        </Box>
        <Box sx={{ textAlign: 'center', py: 2 }}>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            Henüz kart seçilmedi
          </Typography>
          <Button
            variant="contained"
            size="small"
            startIcon={<CreditCardIcon />}
            onClick={onChangeCard}
          >
            Kart Seç
          </Button>
        </Box>
      </Paper>
    );
  }

  const session = card.activeSession;
  const selectedWallet = wallets.find(w => w.walletId === selectedWalletId);
  const displayBalance = selectedWallet ? selectedWallet.balance : (session?.totalBalance || card.wallets?.reduce((sum, w) => sum + w.balance, 0) || 0);
  const remainingBalance = displayBalance - cartTotal;

  return (
    <Paper elevation={1} sx={{ p: 1.5, mb: 1 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.5 }}>
        <Typography variant="subtitle2" sx={{ display: 'flex', alignItems: 'center' }}>
          <CreditCardIcon sx={{ fontSize: '1rem', mr: 0.5 }} />
          Ön Ödemeli Kart
        </Typography>
        <Button
          size="small"
          variant="outlined"
          startIcon={<SwapHorizIcon />}
          onClick={onChangeCard}
          sx={{ fontSize: '0.7rem', py: 0.3, px: 1 }}
        >
          Değiştir
        </Button>
      </Box>

      <Grid container spacing={0.5}>
        <Grid item xs={6}>
          <Box sx={{ border: '1px solid #e0e0e0', p: 0.8, borderRadius: 1, bgcolor: 'grey.50' }}>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', lineHeight: 1.2 }}>
              Kart No
            </Typography>
            <Typography variant="body2" fontWeight={600} sx={{ lineHeight: 1.3 }}>
              {card.cardNumber}
            </Typography>
            {card.cardTypeName && (
              <Chip
                label={card.cardTypeName}
                size="small"
                sx={{
                  mt: 0.3,
                  bgcolor: card.cardTypeColor || '#1976d2',
                  color: '#fff',
                  fontSize: '0.65rem',
                  height: 18
                }}
              />
            )}
          </Box>
        </Grid>

        <Grid item xs={6}>
          <Box sx={{
            border: '2px solid',
            borderColor: displayBalance > 0 ? 'success.main' : 'error.main',
            p: 0.8,
            borderRadius: 1,
            bgcolor: displayBalance > 0 ? 'success.50' : 'error.50',
            height: '100%',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            alignItems: 'center'
          }}>
            <Typography variant="caption" color="text.secondary" sx={{ lineHeight: 1 }}>
              {selectedWallet ? selectedWallet.walletName : 'Bakiye'}
            </Typography>
            <Typography
              variant="h6"
              fontWeight={700}
              sx={{
                color: displayBalance > 0 ? 'success.main' : 'error.main',
                lineHeight: 1.2
              }}
            >
              {formatCurrency(displayBalance)}
            </Typography>
          </Box>
        </Grid>
      </Grid>

      {cartTotal > 0 && (
        <Box sx={{
          mt: 0.5,
          p: 0.6,
          borderRadius: 1,
          bgcolor: remainingBalance >= 0 ? 'info.50' : 'error.50',
          border: '1px solid',
          borderColor: remainingBalance >= 0 ? 'info.main' : 'error.main',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}>
          <Typography variant="caption" sx={{ color: remainingBalance >= 0 ? 'info.main' : 'error.main', fontWeight: 600 }}>
            Sepet: {formatCurrency(cartTotal)}
          </Typography>
          <Typography variant="body2" sx={{ color: remainingBalance >= 0 ? 'info.main' : 'error.main', fontWeight: 700 }}>
            Kalan: {formatCurrency(remainingBalance)}
          </Typography>
        </Box>
      )}

      {session && (
        <>
          <Divider sx={{ my: 0.5 }} />
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexWrap: 'wrap' }}>
            {/* Sahip cari ya da personel olabilir; ownerName backend'de türe göre çözülür. */}
            <Typography variant="caption" color="text.secondary">
              {session.ownerType === 'PERSONNEL' ? 'Personel' : 'Cari'}:{' '}
              <strong>{session.ownerName || session.accountName || '—'}</strong>
            </Typography>
          </Box>
        </>
      )}

      {wallets.length > 1 && (
        <>
          <Divider sx={{ my: 0.5 }} />
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
            Cüzdan Seçin:
          </Typography>
          <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
            {wallets.map((w) => (
              <Chip
                key={w.walletId}
                icon={<AccountBalanceWalletIcon sx={{ fontSize: '0.9rem !important' }} />}
                label={`${w.walletName}: ${formatCurrency(w.balance)}`}
                size="small"
                color={selectedWalletId === w.walletId ? 'primary' : 'default'}
                variant={selectedWalletId === w.walletId ? 'filled' : 'outlined'}
                onClick={() => onWalletSelect(w.walletId)}
                sx={{
                  fontSize: '0.7rem',
                  height: 26,
                  cursor: 'pointer',
                  fontWeight: selectedWalletId === w.walletId ? 700 : 400,
                  '&:hover': { opacity: 0.8 }
                }}
              />
            ))}
          </Box>
        </>
      )}

      {/* Karta tanımlı ön ödemeli ürün hakları (girişte ödenmiş) */}
      {entitlements && entitlements.length > 0 && (
        <>
          <Divider sx={{ my: 0.5 }} />
          <Typography variant="caption" color="text.secondary" sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
            <CardGiftcardIcon sx={{ fontSize: '0.95rem' }} color="success" />
            Tanımlı Ürünler (Girişte ödendi)
          </Typography>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
            {entitlements.map((ent) => {
              // Tamamen kullanılmış hak: pasif "Kullanıldı" olarak gösterilir, sepete eklenemez.
              const fullyUsed = (ent.consumedQuantity || 0) >= (ent.quantity || 0) || ent.status === 'CONSUMED';
              const disabled = fullyUsed || (ent.remaining || 0) <= 0 || !onAddEntitlement;
              return (
                <Box
                  key={`${ent.productId}-${ent.id}`}
                  onClick={() => { if (!disabled && onAddEntitlement) onAddEntitlement(ent); }}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    p: 0.6,
                    borderRadius: 1,
                    border: '1px solid',
                    borderColor: fullyUsed ? 'grey.400' : disabled ? 'grey.300' : 'success.main',
                    bgcolor: fullyUsed ? 'grey.200' : disabled ? 'grey.100' : 'success.50',
                    cursor: disabled ? 'default' : 'pointer',
                    opacity: fullyUsed ? 0.75 : disabled ? 0.6 : 1,
                    '&:hover': disabled ? {} : { bgcolor: 'success.100' },
                  }}
                >
                  <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                    <Typography
                      variant="caption"
                      fontWeight={600}
                      sx={{ lineHeight: 1.2, textDecoration: fullyUsed ? 'line-through' : 'none' }}
                    >
                      {ent.productName}
                    </Typography>
                    <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.65rem' }}>
                      {fullyUsed
                        ? 'Kullanıldı (Girişte ödendi)'
                        : `Kalan hak: ${ent.remaining}${(ent.consumedQuantity || 0) > 0 ? ` · Kullanılan: ${ent.consumedQuantity}` : ''}${ent.unitPricePaid > 0 ? ' · 0 ₺' : ''}`}
                    </Typography>
                  </Box>
                  {fullyUsed ? (
                    <Chip
                      label="Kullanıldı"
                      size="small"
                      sx={{ height: 22, fontSize: '0.65rem', bgcolor: 'grey.500', color: '#fff', '& .MuiChip-label': { px: 0.5 } }}
                    />
                  ) : !disabled ? (
                    <Chip
                      icon={<AddCircleIcon sx={{ fontSize: '0.9rem !important' }} />}
                      label="0 ₺ Ekle"
                      size="small"
                      color="success"
                      sx={{ height: 22, fontSize: '0.65rem', '& .MuiChip-label': { px: 0.5 } }}
                    />
                  ) : null}
                </Box>
              );
            })}
          </Box>
        </>
      )}
    </Paper>
  );
};

export default PrepaidCardPanel;
